import { z } from 'zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Section } from '@expo/html-elements'
import { useTranslation } from 'react-i18next'
import { H2, Text } from '../../components/TextComponents'

import { trpc } from '../../../App'
import { View } from 'react-native'
import { Card, CardBody, CardHeader } from '../../components/Card'
import { Col, Row } from '../../components/Row'
import { dsv } from '../../styles/defaults'

import { RequiredFieldsForm } from '../../forms/requiredFields'
import { addPrefix } from '../../locales/translationKeys'

import { LoadingScreen } from '../../components/LoadingScreen'

import { ProcessTypeSchema } from '@dpa/common/dist'
import { ProcessGateFieldEnumType } from '@dpa/common/dist/prismaGenerated/inputTypeSchemas/ProcessGateFieldEnumSchema'
import { datenblattFromSchema } from '../../forms/datenblattFromSchema'
import { useAuthContext } from '../../hooks/context/useAuthContext'
import ProcessUebergabeStepSchema, {
  ProcessUebergabeStepType,
} from '@dpa/common/dist/prismaGenerated/inputTypeSchemas/ProcessUebergabeStepSchema'
import { GpjButton } from './projects/components/GpjButton'
import { buildRequiredFieldsExcelBlob, downloadBlob } from '../../utils/requiredFieldsExcel'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
// TODO: fixme
const projectArten = [
  'Elektifizierung_AC',
  'Elektifizierung_DC',
  'komplexe_DC_inclIBN',
  'standart_DC_inclIBN',
  'auslieferung_DC',
  'ac_inc_config',
] as const

const hiddenUebergabeSteps = new Set<ProcessUebergabeStepType>(['CompletionOfPlanning2Vertrieb'])
const visibleUebergabeSteps = Object.values(ProcessUebergabeStepSchema.enum).filter(
  (step) => !hiddenUebergabeSteps.has(step)
)

type RequiredFieldName = keyof z.infer<typeof RequiredFieldsForm> & string

const requiredFieldOptions: ProcessGateFieldEnumType[] = ['required', 'optional', 'hidden']

const selectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 360,
    },
  },
}

const requiredFieldSelectSx = {
  width: '100%',
  height: 30,
  backgroundColor: dsv.colors.base,
  color: dsv.colors.neutral,
  fontFamily: dsv.text.fontFamily,
  fontSize: 13,
  '.MuiSelect-select': {
    minHeight: 'auto',
    padding: '4px 28px 4px 8px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: dsv.colors.connectGrau30,
  },
}

const processTypeSelectSx = {
  width: '100%',
  height: 36,
  backgroundColor: dsv.colors.base,
  color: dsv.colors.neutral,
  fontFamily: dsv.text.fontFamily,
  '.MuiSelect-select': {
    padding: '7px 32px 7px 12px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: dsv.colors.connectGrau30,
  },
}

function buildRequiredFieldGroups() {
  const requiredFieldNames = new Set(Object.keys(RequiredFieldsForm.shape))

  return Object.entries(datenblattFromSchema)
    .map(([groupName, group]) => {
      const fields = group.fields.filter((fieldName) => requiredFieldNames.has(fieldName)) as RequiredFieldName[]
      return [groupName, fields] as [string, RequiredFieldName[]]
    })
    .filter(([, fields]) => fields.length > 0)
}

type DraftRequiredFields = Partial<
  Record<ProcessUebergabeStepType, Partial<Record<RequiredFieldName, string | undefined>>>
>

export function SetRequiredFields() {
  const { t } = useTranslation()

  const { authCtx } = useAuthContext()
  const isAllowed = useMemo(() => authCtx?.roles.includes('Admin'), [authCtx?.roles])

  const [processType, setProcessType] = useState<z.infer<typeof ProcessTypeSchema>>(projectArten[0])
  const [draftValues, setDraftValues] = useState<DraftRequiredFields>({})

  const processGates = trpc.processGate.byProcessType.useQuery({ processType: processType })
  const updateProcessGate = trpc.processGate.update.useMutation()

  useEffect(() => {
    setDraftValues({})
  }, [processGates.data, processType])

  const uebergabeSteps = visibleUebergabeSteps

  const downloadRequiredFieldsExcel = useCallback(() => {
    if (!processGates.data) {
      return
    }

    const blob = buildRequiredFieldsExcelBlob(
      processGates.data,
      processType,
      (fieldName) => t(addPrefix('labels', fieldName)),
      (stepName) => t(`uebergabeTitle_${stepName}`),
      (value) => String(value ?? 'Option auswählen'),
      uebergabeSteps
    )

    downloadBlob(blob, `required-fields-${processType}.xlsx`)
  }, [processGates.data, processType, t, uebergabeSteps])

  const fieldGroups = useMemo(() => buildRequiredFieldGroups(), [])

  const saveRequiredField = useCallback(
    (step: ProcessUebergabeStepType, field: RequiredFieldName, value: ProcessGateFieldEnumType) => {
      setDraftValues((current) => ({
        ...current,
        [step]: {
          ...current[step],
          [field]: value,
        },
      }))

      updateProcessGate.mutate({
        field,
        value,
        processType,
        processUebergabeStep: step,
      })
    },
    [processType, updateProcessGate]
  )

  const getRequiredFieldValue = useCallback(
    (step: ProcessUebergabeStepType, field: RequiredFieldName) => {
      return draftValues[step]?.[field] ?? processGates.data?.[step]?.[field] ?? ''
    },
    [draftValues, processGates.data]
  )

  return (
    <Section>
      <Row>
        <Col cols={12}>
          <Card>
            <CardHeader>
              <Text style={dsv.text}>Uebergabe Felder Festlegen</Text>
            </CardHeader>
            <CardBody customStyle={{ minHeight: 300 }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 8,
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                <View style={{ flex: 1 }}>
                  <Select
                    value={processType}
                    onChange={(event: SelectChangeEvent<z.infer<typeof ProcessTypeSchema>>) => {
                      setProcessType(event.target.value as z.infer<typeof ProcessTypeSchema>)
                    }}
                    MenuProps={selectMenuProps}
                    sx={processTypeSelectSx}
                  >
                    {projectArten.map((projectArt) => (
                      <MenuItem key={projectArt} value={projectArt}>
                        {t(projectArt)}
                      </MenuItem>
                    ))}
                  </Select>
                </View>
                <GpjButton
                  text="Excel herunterladen"
                  width={210}
                  onPress={downloadRequiredFieldsExcel}
                  isDisabled={processGates.isLoading || !processGates.data}
                />
              </View>
              {processGates.isLoading || !processGates.data ?
                (
                  <LoadingScreen active />
                ) : !isAllowed ? (
                  <View>
                    <Text>Fehlende Benutzer Berechtigungen, wenden sie sich an einen Administrator</Text>
                  </View>
                ) : (
                  <View>
                    {fieldGroups.map(([groupName, groupEntries]) => (
                      <table
                        key={groupName}
                        style={{
                          textAlign: 'left',
                          marginBottom: '20px',
                          tableLayout: 'fixed',
                          width: '100%',
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: "200px"}}>
                              <H2 style={{ color: dsv.colors.connectGrau7 }}>{groupName}</H2>
                            </th>
                            {uebergabeSteps.map((step) => (
                              <th key={step}  style={{ color: dsv.colors.connectGrau7 }}>
                                {t(`uebergabeTitle_${step}`)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {groupEntries.map((fieldName, fieldIdx) => (
                            <tr
                              key={fieldName}
                              style={{
                                backgroundColor: fieldIdx % 2 === 0 ? dsv.colors.base : dsv.colors.base2,
                              }}
                            >
                              <th scope="row">{t(addPrefix('labels', fieldName))}</th>
                              {uebergabeSteps.map((step) => (
                                <td key={step}>
                                  <Select
                                    value={getRequiredFieldValue(step, fieldName)}
                                    onChange={(event: SelectChangeEvent<string>) => {
                                      const value = event.target.value as ProcessGateFieldEnumType
                                      saveRequiredField(step, fieldName, value)
                                    }}
                                    displayEmpty
                                    MenuProps={selectMenuProps}
                                    renderValue={(selected) => selected === '' ? 'Option auswählen' : t(selected)}
                                    sx={requiredFieldSelectSx}
                                  >
                                    <MenuItem value="" disabled>
                                      Option auswählen
                                    </MenuItem>
                                    {requiredFieldOptions.map((option) => (
                                      <MenuItem key={option} value={option}>
                                        {t(option)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </td>
                              ))}
                            </tr>
                          )
                          )}
                        </tbody>
                      </table>
                    ))}
                  </View>
                )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Section>
  )
}
